import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import buildQuery from "odata-query";
import { Subject, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { SalesOrderLine } from "../../../../core/models/orders/saleOrderLine";
import { CommonModule } from "@angular/common";
import { DropDownDto } from "../../../../core/models/program/dropdownDto";
import { Currency } from "../../../../core/models/shared/currencyList";
import { SamplepoListingService } from "../../../../core/services/samplepo-listing.service";
import { FocusOnShowDirective } from "../../../../core/directives/auto-focus.directive";
import { ExchangeRatesDto } from "../../../../core/models/shared/exchangeRateDto";
import { SelectModule, Select, SelectChangeEvent } from "primeng/select";
import { ReleasedProductVariants } from "../../../../core/models/shared/releasedProductVariants";
import { ReleasedProduct } from "../../../../core/models/shared/releaseProduct";
import { InventoryWarehouseDto } from "../../../../core/models/inventory/inventory-lookup.models";
import { JniStoreDefaultDto } from "../../../../core/models/inventory/inventory-lookup.models";
import { InputTextModule } from "primeng/inputtext";
import { DropdownModule } from "primeng/dropdown";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { DialogModule } from "primeng/dialog";
import { MISC_RELEASED_PRODUCT } from "../../../../shared/constants/misc-constant";

@UntilDestroy()
@Component({
  selector: "jni-order-line-detail",
  templateUrl: "./order-line-detail.component.html",
  styleUrls: ["./order-line-detail.component.scss"],
  imports: [
    CommonModule,
    SelectModule,
    FocusOnShowDirective,
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    ToggleSwitchModule,
    DialogModule,
  ],
  providers: [],
})
export class OrderLineDetailComponent implements OnInit, OnChanges {
  @Input() parent!: FormGroup;
  @Input() currencyList: Currency[] = [];
  @Input() releasedProductsList: ReleasedProduct[] = [];
  @Input() releasedProductsVariantsList: ReleasedProductVariants[] = [];
  @Input() sellCurrencyStatus = true;
  @Input() formErrors!: Record<string, string>;
  @Input() netFieldStatus = true;
  @Input() sellRateValueWithIconStatus = false;
  @Input() userBlockedFromEdit = false;
  @Input() orderLineNumber = 0;
  @Input() salesOrderLine: SalesOrderLine = {} as SalesOrderLine;
  @Input() showCommissionsFields = true;
  @Input() customerAccountNumber = "";
  @Input() customerFinancialDimension = "";
  @Input() selectedItemNumber = "";
  @Input() headerSiteId = "";
  @Input() headerWarehouseId = "";
  @Input() headerLocationId = "";
  @Input() headerStore = "";
  @Input() headerOwner = "";

  @Output() validationErrorEvent = new EventEmitter();
  @Input() isFromCdi = false;
  @Input() salesClasssificationList: DropDownDto[] = [];
  @Input() isSalesClassificationIdRequired = false;
  @Input() orderType = "";
  @Input() addNewLineCase = false;
  @Input() templateServiceLineForm = false;
  @Input() disableConfigFields = false;
  @Input() warehouseList: InventoryWarehouseDto[] = [];
  trackableFieldDisabledValue = "Set Up";

  displayPosition = false;
  position = "bottom-right";
  closable = false;
  showSmallLoader = false;
  currencyConversion?: ExchangeRatesDto;
  sellCurrencyConversion?: ExchangeRatesDto;

  showForeignCurrency = false;

  isoCurrencyCode = "";
  netReteAfterConversion = 0;
  netValueWithIcon = "";
  netConvertToUsd = "";
  netTotalPrice = "";
  netTotalPriceConverted = "";

  sellReteAfterConversion = 0;
  sellRateValueWithIcon = "";
  sellTotalPrice = "";
  sellTotalPriceConverted = "";
  sellConvertToUsd = "";
  sellIsoCurrencyCode = "";
  grossProfit = 0;
  commission = 0;
  labelType = "";

  filteredReleasedProductsList: ReleasedProduct[] = [];
  filteredColors: string[] = [];
  filteredSizes: string[] = [];
  filteredConfigs: string[] = [];
  filteredStyles: string[] = [];
  filteredWarehouseList: InventoryWarehouseDto[] = [];
  @ViewChild("orderedPurchaseQuantityD365")
  focusOnQuantity!: ElementRef<HTMLInputElement>;
  disableNet = false;
  isVendorSelected = false;
  showWarningCategoryChangedDialog = false;
  miscReleasedProduct: ReleasedProduct = MISC_RELEASED_PRODUCT;
  storeDefaultsList: JniStoreDefaultDto[] = [];
  // switchMap cancels any in-flight cost lookup when a newer variant change arrives.
  private netPriceTrigger$ = new Subject<string>();

  constructor(private _samplepoListingService: SamplepoListingService) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.disableTrackableField();
    this.customerFinancialDimensionChanges(changes);
    this.releasedProductsChanges(changes);
    this.orderTypeChanges(changes);
    this.selectedItemNumberChanges(changes);
    this.releasedProductsVariantsListChanges(changes);
    if (changes["warehouseList"]) {
      this.filteredWarehouseList = this.warehouseList.filter(
        w => w.inventoryWarehouseD365 === "Yes"
      );
    }
  }

  ngOnInit(): void {
    this.disableTrackableField();
    this.disabledInitialFields();
    this.filteredWarehouseList = this.warehouseList.filter(
      w => w.inventoryWarehouseD365 === "Yes"
    );
    this._samplepoListingService
      .getAllStoreDefaults()
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.storeDefaultsList = res?.data ?? [];
      });
    if (this.orderType === "inventory-sales") {
      this.labelType = "Item Number";
    } else {
      this.labelType = "Category";
    }
    this.netPriceTrigger$
      .pipe(
        switchMap(odataFilter =>
          this._samplepoListingService
            .getItemAvailPhysicalCost(odataFilter)
            .pipe(catchError(() => of(null)))
        ),
        untilDestroyed(this)
      )
      .subscribe(costResponse => {
        const costPrice = costResponse?.data?.[0]?.CostPrice ?? 0;
        this.onNetConversion(costPrice.toString());
      });
    //this.getSellCurrencyConversion('INR');
    //this.sellCurrencyStatus = true;
  }
  showPositionDialog(position: string) {
    this.position = position;
    this.displayPosition = true;
  }

  customerFinancialDimensionChanges(changes: SimpleChanges) {
    if (changes["customerFinancialDimension"]) {
      this.filterProducts();
    }
  }

  selectedItemNumberChanges(changes: SimpleChanges) {
    if (changes["selectedItemNumber"] && this.selectedItemNumber) {
      this.sizeAndColorFilter();
      this.addAndRemoveMisc();
    }
  }

  addAndRemoveMisc() {
    if (this.selectedItemNumber === "Misc") {
      this.filteredReleasedProductsList.push(this.miscReleasedProduct);
    }
  }

  addMisc() {
    this.filteredReleasedProductsList.push(this.miscReleasedProduct);
  }

  releasedProductsVariantsListChanges(changes: SimpleChanges) {
    if (changes["releasedProductsVariantsList"] && this.selectedItemNumber) {
      this.sizeAndColorFilter();
    }
  }

  sizeAndColorFilter() {
    this.filteredColors = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == this.selectedItemNumber)
          .sort((a, b) => a.colorDisplayOrderD365 - b.colorDisplayOrderD365)
          .map(x => x.colorD365)
      ),
    ];
    this.filteredSizes = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == this.selectedItemNumber)
          .sort(
            (a, b) =>
              a.sizeGlobalDisplayOrderD365 - b.sizeGlobalDisplayOrderD365
          )
          .map(x => x.sizeD365)
      ),
    ];
    // Config field hidden - no longer populated
    // this.filteredConfigs = [
    //   ...new Set(
    //     this.releasedProductsVariantsList
    //       .filter(x => x.itemIdD365 == this.selectedItemNumber)
    //       .sort((a, b) => a.configDisplayOrderD365 - b.configDisplayOrderD365)
    //       .map(x => x.configD365)
    //   ),
    // ];
    this.filteredStyles = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == this.selectedItemNumber)
          .sort((a, b) => a.styleDisplayOrderD365 - b.styleDisplayOrderD365)
          .map(x => x.styleD365)
      ),
    ];

    const color = this.filteredColors[0];
    const size = this.filteredSizes[0];
    const style = this.filteredStyles[0];

    if (color && !this.disableConfigFields) {
      this.parent.get(["lines", "color"])?.enable();
    } else {
      this.parent.get(["lines", "color"])?.disable();
    }

    if (size && !this.disableConfigFields) {
      this.parent.get(["lines", "size"])?.enable();
    } else {
      this.parent.get(["lines", "size"])?.disable();
    }

    // Config field hidden - disable it
    this.parent.get(["lines", "config"])?.disable();

    if (style && !this.disableConfigFields) {
      this.parent.get(["lines", "style"])?.enable();
    } else {
      this.parent.get(["lines", "style"])?.disable();
    }
  }

  releasedProductsChanges(changes: SimpleChanges) {
    if (changes["releasedProductsList"]) {
      if (this.releasedProductsList?.length) {
        this.filterProducts();
      }
    }
  }

  orderTypeChanges(changes: SimpleChanges) {
    if (changes["orderType"]) {
      this.filterProducts();
      if (this.orderType === "inventory-sales") {
        this.labelType = "Item Number";
      } else {
        this.labelType = "Category";
      }
    }
  }

  filterProducts() {
    if (this.orderType == "inventory-sales") {
      this.filteredReleasedProductsList = this.releasedProductsList.filter(
        x =>
          x.productTypeD365 == "Item" &&
          x.financialDimensionD365 &&
          this.customerFinancialDimension &&
          (((x.financialDimensionD365 as string).split("-").pop() ??
            "") as string) ==
            ((this.customerFinancialDimension.split("-").pop() ??
              "") as string) &&
          (((x.financialDimensionD365 as string).split("-").pop() ??
            "") as string) != "-"
      );
      if (this.selectedItemNumber === "Misc") {
        this.addMisc();
      }
    } else if (this.orderType != "inventory-sales") {
      this.filteredReleasedProductsList = this.releasedProductsList.filter(
        x => x.productTypeD365 == "Service" && x.soStopped === "No"
      );
    }
  }

  disableTrackableField() {
    //console.log(this.parent.get(['lines', 'isTrackable'])?.value);
    if (
      this.parent.get(["lines", "itemNumberD365"])?.value ===
      this.trackableFieldDisabledValue
    ) {
      this.parent.get(["lines", "isTrackable"])?.disable();
    }
  }

  logValidationErrors() {
    this.validationErrorEvent.emit();
  }

  disabledInitialFields() {
    this.parent.get(["lines", "netTotalD365"])?.disable();
    this.parent.get(["lines", "currencyCodeD365"])?.disable();
    this.parent.get(["lines", "netConversion"])?.disable();
    this.parent.get(["lines", "netConversionTotal"])?.disable();
    this.parent.get(["lines", "sellTotal"])?.disable();
    this.parent.get(["lines", "sellConversion"])?.disable();
    this.parent.get(["lines", "sellConversionTotal"])?.disable();
  }

  onCurrencyEnable(event: Event | string) {
    let value = false;
    if (typeof event !== "string") {
      const eventTarget = event.target as HTMLInputElement;
      value = eventTarget.checked;
    } else {
      value = event === "checked" ? true : false;
    }
    if (value) {
      //this.parent.get(['lines', 'currencyCodeD365'])?.enable();
      this.parent.get(["lines", "purchasePriceD365"])?.disable();
      this.netFieldStatus = true;
      //this.parent.get(['lines', 'sell'])?.disable();
      this.clearCurrencyCodeRecordAndNetInForeignCurrency();
      this.showForeignCurrency = true;
    } else {
      this.parent.get(["lines", "currencyCodeD365"])?.disable();
      this.parent.get(["lines", "purchasePriceD365"])?.enable();
      //this.parent.get(['lines', 'sell'])?.enable();
      this.clearCurrencyCodeRecordAndNetInForeignCurrency();
      this.currencyConversion = undefined;
      this.showForeignCurrency = false;
      this.netFieldStatus = false;
      this.parent.get(["lines", "currencyCodeD365"])?.setValue("USD");
      this.calculateGrossProfit();
    }
  }

  clearCurrencyCodeRecordAndNetInForeignCurrency() {
    this.parent.patchValue({
      lines: {
        currencyCodeD365: "",
        purchasePriceD365: 0,
        netTotalD365: 0,
        netConversion: "",
        netConversionTotal: "",
        priceInTransactionCurrency: "",
        amountInTransactionCurrency: "",
      },
    });
    this.netValueWithIcon = "";
    this.netConvertToUsd = "";
    this.netTotalPrice = "";
    this.netTotalPriceConverted = "";
  }

  clearNetInForeignCurrency() {
    this.parent.patchValue({
      lines: {
        purchasePriceD365: 0,
        netTotalD365: 0,
        netConversion: "",
        netConversionTotal: "",
        priceInTransactionCurrency: "",
        amountInTransactionCurrency: "",
      },
    });

    this.netValueWithIcon = "";
    this.netConvertToUsd = "";
    this.netTotalPrice = "";
    this.netTotalPriceConverted = "";
  }

  onCurrencyConversion() {
    this.showSmallLoader = true;
    this.clearNetInForeignCurrency();
    const currency = this.parent.get(["lines", "currencyCodeD365"])?.value;
    if (currency) {
      const filter = {
        and: [
          {
            RateTypeName: "Closing",
          },
          {
            FromCurrency: currency,
          },
          {
            ToCurrency: "USD",
          },
        ],
      };
      const query = buildQuery({ filter });

      if (currency === "USD") {
        const checkboxControl = this.parent.get(
          "lines.isnetInForeignCurrencyStatus"
        );
        checkboxControl?.setValue(false);
        this.parent.get(["lines", "currencyCodeD365"])?.disable();
        this.parent.get(["lines", "purchasePriceD365"])?.enable();
        this.currencyConversion = undefined;
        this.showForeignCurrency = false;
        this.netFieldStatus = false;
        this.parent.patchValue({
          lines: {
            netConversion: "",
            netConversionTotal: "",
            priceInTransactionCurrency: "",
          },
        });
        this.setCurrencyCodeToUSD();
        return;
      }
      this._samplepoListingService
        .getCurrencyConversion(query)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this.showSmallLoader = false;
            this.netFieldStatus = false;
            const sortedCurrency = res?.data?.sort((a, b) => {
              if (a.EndDate > b.EndDate) {
                return -1;
              }
              if (a.EndDate < b.EndDate) {
                return 1;
              }
              return 0;
            });
            if (sortedCurrency?.length > 0) {
              this.currencyConversion = sortedCurrency[0];
            }
            if (this.currencyConversion) {
              this.patchValueInRates(this.currencyConversion.Rate.toString());
            }
            this.parent.get(["lines", "purchasePriceD365"])?.enable();
            // this.parent.get(['lines', 'sell'])?.enable();
          },
          error: () => {
            this.showSmallLoader = false;
          },
        });
    }
  }

  newCurrencyConversion() {
    this.showSmallLoader = true;
    const currency = this.parent.get(["lines", "currencyCodeD365"])?.value;
    if (currency) {
      const filter = {
        and: [
          {
            RateTypeName: "Closing",
          },
          {
            FromCurrency: currency,
          },
          {
            ToCurrency: "USD",
          },
        ],
      };
      const query = buildQuery({ filter });
      this._samplepoListingService
        .getCurrencyConversion(query)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this.showSmallLoader = false;
            this.netFieldStatus = false;
            const sortedCurrency = res?.data?.sort((a, b) => {
              if (a.EndDate > b.EndDate) {
                return -1;
              }
              if (a.EndDate < b.EndDate) {
                return 1;
              }
              return 0;
            });
            this.currencyConversion = sortedCurrency[0];
            if (this.currencyConversion) {
              this.patchValueInRates(this.currencyConversion.Rate.toString());
            }
            this.parent.get(["lines", "purchasePriceD365"])?.enable();
            this.netFieldStatus = true;
            this.showForeignCurrency = true;
            this.netFieldStatus = false;
            //this.parent.get(['lines', 'currencyCodeD365'])?.enable();
          },
          error: () => {
            this.showSmallLoader = false;
          },
        });
    }
  }

  getSellCurrency(sellCurrency: string) {
    this.getSellCurrencyConversion(sellCurrency);
  }

  itemSelected(event: SelectChangeEvent, lineRecIdD365?: number | null) {
    if (lineRecIdD365) {
      this.showWarningCategoryChangedDialog = true;
    }

    this.filteredColors = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == event.value)
          .sort((a, b) => a.colorDisplayOrderD365 - b.colorDisplayOrderD365)
          .map(x => x.colorD365)
      ),
    ];
    this.filteredSizes = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == event.value)
          .sort(
            (a, b) =>
              a.sizeGlobalDisplayOrderD365 - b.sizeGlobalDisplayOrderD365
          )
          .map(x => x.sizeD365)
      ),
    ];
    // Config field hidden - no longer populated
    // this.filteredConfigs = [
    //   ...new Set(
    //     this.releasedProductsVariantsList
    //       .filter(x => x.itemIdD365 == event.value)
    //       .sort((a, b) => a.configDisplayOrderD365 - b.configDisplayOrderD365)
    //       .map(x => x.configD365)
    //   ),
    // ];
    this.filteredStyles = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == event.value)
          .sort((a, b) => a.styleDisplayOrderD365 - b.styleDisplayOrderD365)
          .map(x => x.styleD365)
      ),
    ];

    const categoryName =
      this.filteredReleasedProductsList.find(
        x => x.itemNumberD365 == event.value
      )?.nameD365 ?? "";

    const color = this.filteredColors[0];
    const size = this.filteredSizes[0];
    const style = this.filteredStyles[0];

    this.parent.patchValue({
      lines: {
        color: color,
      },
    });
    this.parent.patchValue({
      lines: {
        size: size,
      },
    });
    // Config field hidden - no longer patched
    // this.parent.patchValue({
    //   lines: {
    //     config: config,
    //   },
    // });
    this.parent.patchValue({
      lines: {
        style: style,
      },
    });

    if (color) {
      this.parent.get(["lines", "color"])?.enable();
    } else {
      this.parent.get(["lines", "color"])?.disable();
    }

    if (size) {
      this.parent.get(["lines", "size"])?.enable();
    } else {
      this.parent.get(["lines", "size"])?.disable();
    }

    // Config field hidden - always disabled
    this.parent.get(["lines", "config"])?.disable();

    if (style) {
      this.parent.get(["lines", "style"])?.enable();
    } else {
      this.parent.get(["lines", "style"])?.disable();
    }

    this.parent.patchValue({
      lines: {
        categoryName,
      },
    });
    this.generateDescription();
    this.setNetPrice();
    this.setSellPrice();
  }

  colorSelected() {
    this.generateDescription();
    this.setNetPrice();
  }
  sizeSelected() {
    this.generateDescription();
    this.setNetPrice();
  }
  styleSelected() {
    this.generateDescription();
    this.setNetPrice();
  }
  configSelected() {
    this.generateDescription();
    this.setNetPrice();
  }
  warehouseSelected() {
    this.setNetPrice();
  }

  setNetPrice() {
    const itemNumber = this.parent.get(["lines", "itemNumberD365"])?.value;
    if (!itemNumber) {
      this.onNetConversion("0");
      return;
    }

    const warehouse = this.parent.get(["lines", "warehouse"])?.value ?? "";
    const site =
      this.warehouseList.find(w => w.warehouseIdD365 == warehouse)
        ?.siteIdD365 ?? "";

    let location = "";
    if (this.headerStore && warehouse) {
      location = this.resolveDefaultLocation(
        this.headerStore,
        warehouse,
        this.headerOwner
      );
    }

    const filter = {
      ItemId: itemNumber,
      ColorId: this.parent.get(["lines", "color"])?.value ?? "",
      StyleId: this.parent.get(["lines", "style"])?.value ?? "",
      SizeId: this.parent.get(["lines", "size"])?.value ?? "",
      configId: "",
      SiteId: site,
      Warehouse: warehouse,
      LocationId: location,
    };
    const odataFilter = buildQuery({ filter });
    this.netPriceTrigger$.next(odataFilter);
  }

  resolveDefaultLocation(
    store: string,
    warehouse: string,
    owner: string
  ): string {
    if (owner) {
      const ownerMatch = this.storeDefaultsList.find(
        x =>
          x.storeD365 === store &&
          x.warehouseD365 === warehouse &&
          x.ownerD365 === owner
      );
      if (ownerMatch) {
        return ownerMatch.locationD365 ?? "";
      }
    }

    const baseMatch = this.storeDefaultsList.find(
      x =>
        x.storeD365 === store && x.warehouseD365 === warehouse && !x.ownerD365
    );
    return baseMatch?.locationD365 ?? "";
  }

  setSellPrice() {
    const itemNumber = this.parent.get(["lines", "itemNumberD365"])?.value;
    const products = this.releasedProductsList.filter(
      x => x.itemNumberD365 == itemNumber
    );

    const salesPrice = products?.[0]?.salesPriceD365 as
      | number
      | string
      | undefined;
    this.onSellConversion(salesPrice?.toString() ?? "0");
  }

  generateDescription() {
    if (this.orderType == "inventory-sales") {
      const itemNumber = this.parent.get(["lines", "itemNumberD365"])?.value;
      const categoryName =
        this.filteredReleasedProductsList.find(
          x => x.itemNumberD365 == itemNumber
        )?.nameD365 ??
        this.parent.get(["lines", "categoryName"])?.value ??
        "";
      this.parent.patchValue({
        lines: {
          lineDescriptionD365: `${
            categoryName ?? ""
          }${this.parent.get(["lines", "color"])?.value ? "-" : ""}${
            this.parent.get(["lines", "color"])?.value ?? ""
          }${this.parent.get(["lines", "style"])?.value ? "-" : ""}${
            this.parent.get(["lines", "style"])?.value ?? ""
          }${this.parent.get(["lines", "size"])?.value ? "-" : ""}${
            this.parent.get(["lines", "size"])?.value ?? ""
          }`,
        },
      });
    }
  }

  getSellCurrencyConversion(currency: string) {
    this.showSmallLoader = true;
    if (currency) {
      const filter = {
        and: [
          {
            RateTypeName: "Closing",
          },
          {
            FromCurrency: currency,
          },
          {
            ToCurrency: "USD",
          },
        ],
      };
      const query = buildQuery({ filter });
      this._samplepoListingService
        .getCurrencyConversion(query)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this.showSmallLoader = false;
            const sortedCurrency = res?.data?.sort((a, b) => {
              if (a.EndDate > b.EndDate) {
                return -1;
              }
              if (a.EndDate < b.EndDate) {
                return 1;
              }
              return 0;
            });

            if (sortedCurrency?.length > 0) {
              this.sellCurrencyConversion = sortedCurrency[0];
            }

            // if (this.currencyConversion) {
            //   this.patchValueInRates(this.currencyConversion.Rate.toString());
            // }
            //this.parent.get(['lines', 'purchasePriceD365'])?.enable();
            // this.parent.get(['lines', 'sell'])?.enable();
          },
          error: () => {
            this.showSmallLoader = false;
          },
        });
    }
  }
  editTrackableField(event: string) {
    //this.parent.get(['lines', 'isTrackable'])?.setValue(false);
    if (event === this.trackableFieldDisabledValue) {
      this.parent.get(["lines", "isTrackable"])?.setValue(false);
      this.parent.get(["lines", "isTrackable"])?.disable();
    } else {
      this.parent.get(["lines", "isTrackable"])?.setValue(true);
      this.parent.get(["lines", "isTrackable"])?.enable();
    }
  }
  onQuantityChange(event: Event) {
    const eventTarget = event.target as HTMLInputElement;
    this.showSmallLoader = true;
    let quantity = eventTarget.value.toString().replace(/[^-0-9]/g, "");
    quantity = quantity.includes("-")
      ? quantity.substring(0, 10)
      : quantity.substring(0, 9);
    if (quantity) {
      this.patchValueInQuantity(quantity);
      let netPrice: string = this.parent.get([
        "lines",
        "purchasePriceD365",
      ])?.value;
      if (netPrice) {
        netPrice = netPrice.toString().replace(/[^a-zA-Z0-9.]/g, "");
        const convertedNetPrice: string = this.netConvertToUsd.replace(
          /[^a-zA-Z0-9.]/g,
          ""
        );
        this.patchValueInTotalNet(netPrice, convertedNetPrice);
      }
      let sellPrice: string = this.parent.get(["lines", "sell"])?.value;
      if (sellPrice) {
        sellPrice = sellPrice.toString().replace(/[^a-zA-Z0-9.]/g, "");
        const convertedSellPrice: string = this.sellConvertToUsd
          .toString()
          .replace(/[^a-zA-Z0-9.]/g, "");
        this.patchValueInTotalSell(sellPrice, convertedSellPrice);
      }
      this.showSmallLoader = false;
    } else {
      this.showSmallLoader = false;
      this.clearlinePriceValue();
      this.clearSellLine();
      this.patchEmptyValueInQuantity();
    }
    this.calculateGrossProfit();
  }

  patchEmptyValueInQuantity() {
    this.parent.patchValue({
      lines: {
        orderedPurchaseQuantityD365: "",
      },
    });
  }

  patchValueInQuantity(quantity: string) {
    this.parent.patchValue({
      lines: {
        orderedPurchaseQuantityD365: parseFloat(quantity),
      },
    });
  }

  onNetConversion(event: Event | string) {
    this.showSmallLoader = true;
    let netRate = "";
    if (typeof event !== "string") {
      const eventTarget = event.target as HTMLInputElement;
      netRate =
        this.orderType == "inventory-sales"
          ? eventTarget.value.replace(/[^.0-9.0-9]/g, "")
          : eventTarget.value.replace(/[^.0-9.0-9]/g, "");
    } else {
      netRate =
        this.orderType == "inventory-sales"
          ? event.replace(/[^.0-9.0-9]/g, "")
          : event.replace(/[^.0-9.0-9]/g, "");
    }
    const regExp = /^\d*\.?\d*$/;
    const revenue = regExp.test(netRate) ? "valid" : "invalid";
    if (revenue === "valid" && netRate !== "") {
      netRate = netRate.substring(0, 9);
      this.patchValueInNetPrice(netRate);
      if (netRate) {
        this.netValueWithIcon = netRate;
        const currency = this.parent.get(["lines", "currencyCodeD365"])?.value;
        this.isoCurrencyCode = currency;
        if (this.currencyConversion) {
          if (this.currencyConversion.Rate != 0) {
            this.netReteAfterConversion =
              parseFloat(netRate) * this.currencyConversion?.Rate;
            if (this.netReteAfterConversion.toString() != "") {
              this.netConvertToUsd = this.netReteAfterConversion.toString();
              this.patchValueInNet(this.netConvertToUsd);
            }
          }
        }

        this.patchValueInTotalNet(
          netRate,
          this.netReteAfterConversion.toString()
        );
      }
      this.calculateGrossProfit();
    } else {
      this.clearlinePriceValue();
      if (typeof event !== "string") {
        const eventTarget = event.target as HTMLInputElement;
        eventTarget.value = "";
      }
    }

    this.showSmallLoader = false;
  }

  patchValueInNet(netConvertToUsd: string) {
    this.parent.patchValue({
      lines: {
        netConversion: netConvertToUsd,
        priceInTransactionCurrency: netConvertToUsd,
      },
    });
  }

  patchValueInNetPrice(netPrice: string) {
    this.parent.patchValue({
      lines: {
        purchasePriceD365: parseFloat(netPrice),
        priceInTransactionCurrency: netPrice,
      },
    });
  }

  patchValueInLines(saleorder: SalesOrderLine, sellCurrencyStatus: string) {
    this.isoCurrencyCode = saleorder.currencyCodeD365 ?? "";
    this.netValueWithIcon = saleorder.purchasePriceD365?.toString() ?? "";
    this.netConvertToUsd = saleorder.netConversion?.toString() ?? "";
    this.netTotalPrice = saleorder.netTotalD365?.toString() ?? "";
    this.netTotalPriceConverted =
      saleorder.netConversionTotalD365?.toString() ?? "";

    this.sellRateValueWithIcon = saleorder.sellD365?.toString() ?? "";
    this.sellTotalPrice = saleorder.sellTotalD365 ?? "";
    this.sellTotalPriceConverted = saleorder.sellConversionTotal ?? "";
    this.sellConvertToUsd = saleorder.sellConversion ?? "";
    this.sellIsoCurrencyCode = sellCurrencyStatus ?? "";
    this.grossProfit = saleorder.grossProfitD365 ?? 0;
    this.commission = saleorder.commissionD365 ?? 0;
    if (saleorder.isnetInForeignCurrencyStatus) {
      this.newCurrencyConversion();
    }
  }
  clearlinePriceValue() {
    this.parent.patchValue({
      lines: {
        purchasePriceD365: 0,
        netTotalD365: 0,
        netConversion: "",
        netConversionTotal: "",
        priceInTransactionCurrency: "",
        amountInTransactionCurrency: "",
      },
    });
    this.netTotalPrice = "";
    this.netValueWithIcon = "";
    this.netConvertToUsd = "";
    this.netTotalPriceConverted = "";

    this.clearCommissionAndGrossProfit();
  }

  clearLineForm(clearCurrencyCode = true) {
    if (clearCurrencyCode) {
      this.isoCurrencyCode = "";
    }

    this.netValueWithIcon = "";
    this.netConvertToUsd = "";
    this.netTotalPrice = "";
    this.netTotalPriceConverted = "";
    this.sellRateValueWithIcon = "";
    this.sellTotalPrice = "";
    this.sellTotalPriceConverted = "";
    this.sellConvertToUsd = "";
    this.sellIsoCurrencyCode = "";
    this.grossProfit = 0;
    this.commission = 0;
    this.currencyConversion = undefined;
    this.netReteAfterConversion = 0;
  }

  disabledNetConversionCurrencyField() {
    this.parent.get(["lines", "currencyCodeD365"])?.disable();
    this.parent.get(["lines", "isnetInForeignCurrencyStatus"])?.disable();
    this.netFieldStatus = true;
  }

  disabledNetField() {
    this.parent.get(["lines", "currencyCodeD365"])?.disable();
  }

  enableNetField() {
    this.netFieldStatus = false;
  }

  patchValueInTotalNet(net: string, convertedNet: string) {
    const quantity = this.parent.get([
      "lines",
      "orderedPurchaseQuantityD365",
    ])?.value;
    if (typeof quantity == "number" || quantity) {
      const netprice = parseFloat(net) * parseInt(quantity);
      this.netTotalPrice = netprice.toString();
      const netPriceCon =
        parseFloat(convertedNet == "" ? "0" : convertedNet) *
        parseInt(quantity);
      this.netTotalPriceConverted = netPriceCon.toString();
      this.patchValueInNetTotalNet(
        this.netTotalPrice,
        this.netTotalPriceConverted
      );
    }
  }

  patchValueInNetTotalNet(
    netTotalPrice: string,
    netTotalPriceConverted: string
  ) {
    this.parent.patchValue({
      lines: {
        netTotalD365: parseFloat(netTotalPrice),
        netConversionTotal: netTotalPriceConverted,
        amountInTransactionCurrency:
          netTotalPriceConverted === "0"
            ? netTotalPrice
            : netTotalPriceConverted,
      },
    });
  }

  patchValueInRates(rates: string) {
    this.parent.patchValue({
      lines: {
        rates: parseFloat(rates),
      },
    });
  }

  onSellConversion(event: Event | string) {
    this.showSmallLoader = true;
    let sellRate = "";
    if (typeof event !== "string") {
      const eventTarget = event.target as HTMLInputElement;
      sellRate =
        this.orderType == "inventory-sales"
          ? eventTarget.value.replace(/[^.0-9.0-9]/g, "")
          : eventTarget.value.replace(/[^.0-9.0-9]/g, "");
    } else {
      sellRate =
        this.orderType == "inventory-sales"
          ? event.replace(/[^.0-9.0-9]/g, "")
          : event.replace(/[^.0-9.0-9]/g, "");
    }

    const regExp = /^\d*\.?\d*$/;
    const revenue = regExp.test(sellRate) ? "valid" : "invalid";

    if (revenue === "valid" && sellRate !== "") {
      sellRate = sellRate.substring(0, 9);
      this.patchValueInSellPrice(sellRate);
      if (sellRate) {
        this.sellRateValueWithIcon = sellRate;
        this.sellIsoCurrencyCode =
          this.sellCurrencyConversion?.FromCurrency ?? "";
        if (this.sellCurrencyConversion) {
          if (this.sellCurrencyConversion.Rate != 0) {
            this.sellReteAfterConversion =
              parseFloat(sellRate) * this.sellCurrencyConversion?.Rate;
            if (this.sellReteAfterConversion.toString() != "") {
              this.sellConvertToUsd = this.sellReteAfterConversion.toString();
              this.patchValueInSell(this.sellConvertToUsd);
            }
          }
        }
        this.patchValueInTotalSell(
          sellRate,
          this.sellReteAfterConversion.toString()
        );
      }
      this.calculateGrossProfit();
    } else {
      this.clearSellLine();
      if (typeof event !== "string") {
        const eventTarget = event.target as HTMLInputElement;
        eventTarget.value = "";
      }
    }

    this.showSmallLoader = false;
  }

  clearSellLine() {
    this.parent.patchValue({
      lines: {
        sell: 0,
        sellTotal: "",
        sellConversion: "",
        sellConversionTotal: "",
      },
    });
    this.sellRateValueWithIcon = "";
    this.sellTotalPrice = "";
    this.sellTotalPriceConverted = "";
    this.sellConvertToUsd = "";
    this.clearCommissionAndGrossProfit();
  }

  clearCommissionAndGrossProfit() {
    this.parent.patchValue({
      lines: {
        commission: 0,
        grossProfit: 0,
      },
    });
    this.commission = 0;
    this.grossProfit = 0;
  }
  patchValueInSell(sellConvertToUsd: string) {
    this.parent.patchValue({
      lines: {
        sellConversion: sellConvertToUsd,
      },
    });
  }
  patchValueInSellPrice(sellPrice: string) {
    this.parent.patchValue({
      lines: {
        sell: parseFloat(sellPrice),
      },
    });
  }

  patchValueInTotalSell(sell: string, convertedSell: string) {
    const quantity = this.parent.get([
      "lines",
      "orderedPurchaseQuantityD365",
    ])?.value;

    if (typeof quantity == "number" || quantity) {
      const sellPrice = parseFloat(sell) * parseInt(quantity);
      this.sellTotalPrice = sellPrice.toString();

      const sellPriceCon =
        parseFloat(convertedSell == "" ? "0" : convertedSell) *
        parseInt(quantity);
      this.sellTotalPriceConverted = sellPriceCon.toString();

      this.patchValueInNetTotalSell(
        this.sellTotalPrice,
        this.sellTotalPriceConverted
      );
    }
  }

  patchValueInNetTotalSell(
    sellTotalPrice: string,
    sellTotalPriceConverted: string
  ) {
    this.parent.patchValue({
      lines: {
        sellTotal: sellTotalPrice,
        sellConversionTotal: sellTotalPriceConverted,
      },
    });
  }

  calculateGrossProfit() {
    const netTotal = this.parent.get(["lines", "netTotalD365"])?.value;
    const sellTotal = this.parent.get(["lines", "sellTotal"])?.value;

    if (parseFloat(netTotal) && parseFloat(sellTotal)) {
      const grossProfit =
        ((parseFloat(sellTotal) - parseFloat(netTotal)) /
          parseFloat(sellTotal)) *
        100;
      const grossProfitRound = grossProfit.toFixed(2).toString();
      this.grossProfit = parseFloat(grossProfitRound);
      this.parent.patchValue({
        lines: {
          grossProfit: parseFloat(grossProfitRound),
        },
      });
      const commission = (parseFloat(sellTotal) - parseFloat(netTotal)) * 0.5;
      const commissionRound = commission.toFixed(2).toString();
      this.commission = parseFloat(commissionRound);
      this.parent.patchValue({
        lines: {
          commission: parseFloat(commissionRound),
        },
      });
    } else {
      this.grossProfit = 0;
      this.parent.patchValue({
        lines: {
          grossProfit: 0,
        },
      });
      this.commission = 0;
      this.parent.patchValue({
        lines: {
          commission: 0,
        },
      });
    }
  }

  applyVendorSkuLengthValidation(event: Event) {
    const eventTarget = event.target as HTMLInputElement;
    if (eventTarget.value) {
      this.parent.patchValue({
        lines: {
          externalItemNumberD365: eventTarget.value.substring(0, 40),
        },
      });
    }
  }

  @ViewChild("currencySelect") currencySelect!: Select;

  setCurrencyCodeToUSD() {
    this.parent.get(["lines", "currencyCodeD365"])?.setValue("USD");
    this.currencySelect.disabled = true;
  }

  applyNickNameLengthValidation(event: Event) {
    const eventTarget = event.target as HTMLInputElement;
    if (eventTarget.value) {
      this.parent.patchValue({
        lines: {
          nickName: eventTarget.value.substring(0, 200),
        },
      });
    }
  }
  // onItemSelectionChange(event: any) {
  //   this.editTrackableField(event);
  //   if (event) {
  //     const hasValue = this.parent.get(['lines', 'nickName'])?.value;
  //     const isReleasedProduct = this.releasedProductsList.filter((obj) => {
  //       if (hasValue === obj.itemNumberD365) {
  //         return true;
  //       } else {
  //         return false;
  //       }
  //     });
  //     if (!hasValue || hasValue === '' || isReleasedProduct.length > 0) {
  //       this.parent.patchValue({
  //         lines: {
  //           nickName: event
  //         }
  //       });
  //     }
  //   }
  // }

  changeFocusToQuantity() {
    setTimeout(() => {
      this.focusOnQuantity.nativeElement.focus();
    });
  }

  isVendorSelectedEvent(value: boolean) {
    this.isVendorSelected = value;
    if (!this.isVendorSelected) {
      this.clearNetInForeignCurrency();
    }
  }

  disableNetIfVendorNotfound() {
    if (!this.isVendorSelected) {
      return true;
    }
    return false;
  }

  okCategoryChangedDialog() {
    this.showWarningCategoryChangedDialog = false;
    this.showSmallLoader = false;
  }
}
